import * as React from 'react';
import { cn } from '../../../../components/ui';
import { supernovaColor } from '../types';

interface ChatTypingIndicatorProps {
  className?: string;
}

// Define the keyframes animation
const pulseScaleAnimation = `
  @keyframes pulseScale {
    0%, 100% { transform: scale(0.8); opacity: 0.5; }
    50% { transform: scale(1.2); opacity: 1; }
  }
`;

/**
 * Chat Typing Indicator Component
 * 
 * Displays a typing indicator for the Nova AI Chat when the LLM is generating a response.
 */
export function ChatTypingIndicator({ className = '' }: ChatTypingIndicatorProps) {
  // Add the animation to the document head
  React.useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.innerHTML = pulseScaleAnimation;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);
  
  return (
    <div 
      className={cn(
        "flex flex-col rounded-lg p-3 mr-auto relative overflow-hidden",
        "bg-card border border-border",
        className
      )}
    >
      {/* Very subtle left accent */}
      <div 
        className="absolute left-0 top-0 bottom-0 w-[2px] bg-gradient-to-b from-supernova-purple/30 to-supernova-blue/20 opacity-70"
      />
      
      <div className="flex items-center space-x-2 pl-2 relative z-10">
        <div className="flex space-x-1 items-center">
          <div 
            className="h-1.5 w-1.5 rounded-full bg-primary/70"
            style={{ 
              animation: 'pulseScale 1.5s infinite ease-in-out',
              animationDelay: '0ms'
            }}
          ></div>
          <div 
            className="h-1.5 w-1.5 rounded-full bg-primary/70"
            style={{ 
              animation: 'pulseScale 1.5s infinite ease-in-out',
              animationDelay: '300ms'
            }}
          ></div>
          <div 
            className="h-1.5 w-1.5 rounded-full bg-primary/70"
            style={{ 
              animation: 'pulseScale 1.5s infinite ease-in-out',
              animationDelay: '600ms'
            }}
          ></div>
        </div>
        <span className="text-xs font-medium text-foreground">
          Nova is thinking...
        </span>
      </div>
    </div>
  );
} 